'use client';

import { Board, PendingSlapList, SlapId, NewSlapOnchain, OnchainPaymentMethod, Objective, PlaceId, StickerDesignId } from '../utils/Primitives'
import {  useAccount, useReadContract, useWriteContract, useTransactionReceipt } from 'wagmi'
import { zeroAddress, hexToNumber } from 'viem'
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { abi } from './StickerChainAbi'
const StickerChainContractAddress = process.env.NEXT_PUBLIC_STICKER_CHAIN_CONTRACT_ADDRESS as `0x${string}` | null;
import { setDraftSlapPlayer } from './Slaps'
import { SlapWarning } from './SlapCheckWarnings'

import SlapPublishSharePanel from './SlapPublishSharePanel'
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { getStickerDesign } from '../stickers/StickerDesigns'
import { buildLocateUrl, buildViewUrl } from '../utils/shareLinks'
import { loadFarcasterSdk } from '../utils/farcasterSdk'

export type SlapExecutooorProps = {
    pendingSlaps: PendingSlapList | null
    boardRef: MutableRefObject<Board>
    setOpenTimestamp : number;
    handleDeletePendingSlap: (slapId: SlapId) => void;
    handleDidSubmitTransaction: () => void;
    addSlapActivity: (slapId: SlapId, placeId: PlaceId, stickerId: StickerDesignId, size: number, isUserCreated?: boolean) => void;
    renderTriggerButton?: boolean;
    autoExecuteOnOpen?: boolean;
    isMiniApp?: boolean;
    suppressDialogOpen?: boolean;
    onDialogOpenChange?: (isOpen: boolean) => void;
    onShareOverlayChange?: (isOpen: boolean) => void;
    onPreCheckWarningsChange?: (warnings: SlapWarning[]) => void;
}

export default function SlapExecutooor(props: SlapExecutooorProps) {
    const { address: playerAddress } = useAccount()
    const {
        boardRef,
        pendingSlaps,
        setOpenTimestamp,
        handleDeletePendingSlap,
        handleDidSubmitTransaction,
        addSlapActivity,
        autoExecuteOnOpen,
        isMiniApp = false,
        suppressDialogOpen = false,
        onDialogOpenChange,
        onShareOverlayChange,
        onPreCheckWarningsChange,
    } = props;
    const [open, setOpen] = useState(false)
    const [lastOpenedAt, setLastOpenedAt] = useState(0)
    const [totalBaseTokenCost, setTotalBaseTokenCost] = useState<bigint>(BigInt(0))
    const [totalCosts, setTotalCosts] = useState<OnchainPaymentMethod[]>([])
    const [selectedObjectives, setSelectedObjectives] = useState<bigint[]>([])
    const [publishStage, setPublishStage] = useState<'new' | 'awaiting_tx' | 'tx_sent' | 'published'>('new');
    const [transactionError, setTransactionError] = useState<string | null>(null);
    const [preCheckWarnings, setPreCheckWarnings] = useState<SlapWarning[]>([])
    const [publishedSlapId, setPublishedSlapId] = useState<SlapId | null>(null);
    const [shareStickerName, setShareStickerName] = useState<string | null>(null);
    const [sharePublisherEns, setSharePublisherEns] = useState<string | null>(null);
    const [sharePreviewStatus, setSharePreviewStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
    const [sharePreviewUrl, setSharePreviewUrl] = useState<string | null>(null);
    const [shareComposerBusy, setShareComposerBusy] = useState(false);
    const [shareCheckCount, setShareCheckCount] = useState(0);
    const [shareNextCheckIn, setShareNextCheckIn] = useState<number | null>(null);
    const [shareResourceUrl, setShareResourceUrl] = useState<string | null>(null);
    const [showSharePrompt, setShowSharePrompt] = useState(false);
    const [shareTimedOut, setShareTimedOut] = useState(false);
    const previewPollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const previewCountdownInterval = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollStartedAtRef = useRef<number | null>(null);
    const lastAutoExecuteRef = useRef<number>(0);
    const siteOrigin = useMemo(() => (
        typeof window !== 'undefined' ? window.location.origin : ''
    ), []);

    const handleCloseSharePanel = useCallback(() => {
        setShowSharePrompt(false);
        if (pendingSlaps && pendingSlaps.length > 0) {
            handleDeletePendingSlap(pendingSlaps[0].id);
        }
        setPublishedSlapId(null);
        setOpen(false);
    }, [pendingSlaps, handleDeletePendingSlap]);

    const {
        reset: resetSlapTransaction,
        status: slapTransactionStatus,
        error,
        failureCount,
        failureReason,
        data: slapTransactionHash,
        writeContract,
        isSuccess: slapTransactionIsSuccess
    } = useWriteContract();

    const { data: slapTransactionResult, isSuccess: slapTransactionHashIsFetched } = useTransactionReceipt({
        hash: slapTransactionHash,
        query: {
            enabled: slapTransactionIsSuccess,
            // Prevent continuous polling
            refetchInterval: 0,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false
        }
    });

    // Add a ref to track processed transaction hashes
    const processedTxHashesRef = useRef<Set<string>>(new Set());

    // Reset error when dialog is closed
    useEffect(() => {
        if (!open) {
            setTransactionError(null);
        }
    }, [open]);

    useEffect(() => {
        if (onDialogOpenChange) {
            onDialogOpenChange(open);
        }
    }, [onDialogOpenChange, open]);

    useEffect(() => {
        if (open) {
            return;
        }
        if (showSharePrompt || publishedSlapId !== null || sharePreviewStatus !== 'idle') {
            return;
        }
        if (isMiniApp && publishedSlapId && pendingSlaps && pendingSlaps.length > 0) {
            handleDeletePendingSlap(pendingSlaps[0].id);
        }
        if (previewPollTimeout.current) {
            clearTimeout(previewPollTimeout.current);
            previewPollTimeout.current = null;
        }
        if (previewCountdownInterval.current) {
            clearInterval(previewCountdownInterval.current);
            previewCountdownInterval.current = null;
        }
        setPublishedSlapId(null);
        setShareStickerName(null);
        setSharePublisherEns(null);
        setSharePreviewStatus('idle');
        setSharePreviewUrl(null);
        setShareCheckCount(0);
        setShareNextCheckIn(null);
        setShareResourceUrl(null);
        setShareComposerBusy(false);
        setShareTimedOut(false);
        pollStartedAtRef.current = null;
    }, [
        open,
        suppressDialogOpen,
        showSharePrompt,
        isMiniApp,
        pendingSlaps,
        publishedSlapId,
        handleDeletePendingSlap,
        sharePreviewStatus
    ]);

    useEffect(() => {
        if (!isMiniApp || !publishedSlapId || !pendingSlaps || pendingSlaps.length === 0) {
            return;
        }
        handleDeletePendingSlap(pendingSlaps[0].id);
    }, [isMiniApp, publishedSlapId, pendingSlaps, handleDeletePendingSlap]);

    const pendingSlapsOnchainFormat = useCallback((pendingSlaps: PendingSlapList) : NewSlapOnchain[] => {
        return pendingSlaps.map(item => ({
            placeId: BigInt(item.place.id),
            stickerId: BigInt(item.sticker.id),
            size: BigInt(item.size ? item.size : 1)
        }))
    }, [])
    const slapSpecs : NewSlapOnchain[] = pendingSlaps ? pendingSlapsOnchainFormat(pendingSlaps) : []
    const anySlaps : boolean = slapSpecs.length > 0
    const enableRead : boolean = (anySlaps && StickerChainContractAddress !== null) ? true : false

    const { data: costOfSlapsData,
            isFetched: costOfSlapsIsFetched,
            error: costOfSlapsError,
            isError: costOfSlapsIsError  } = useReadContract({
        abi,
        address: StickerChainContractAddress || '0x0',
        functionName: 'costOfSlaps',
        query: {enabled: enableRead},
        args: [zeroAddress, slapSpecs, selectedObjectives]
    })

    if (costOfSlapsIsError) {
        console.log(`Error fetching cost of slaps: ${costOfSlapsError}`)
    }
    useEffect(() => {
        if (costOfSlapsIsFetched && costOfSlapsData && !costOfSlapsIsError) {
            console.log(`Cost of Slaps fetched: ${costOfSlapsData[0].total}`)
            const paymentTotals : OnchainPaymentMethod[] = costOfSlapsData.map(item => ({
                total: item.total,
                paymentMethodId: item.paymentMethodId,
            }))
            console.log('Payment totals:', paymentTotals.map(pt => ({
                total: pt.total.toString(),
                paymentMethodId: pt.paymentMethodId.toString()
            })))
            setTotalCosts(paymentTotals)
            if (costOfSlapsData[0].paymentMethodId == BigInt(0)) {
                setTotalBaseTokenCost(costOfSlapsData[0].total)
            }
        }
    },
    [costOfSlapsIsFetched, costOfSlapsData, costOfSlapsIsError])

    useEffect(() => {
        if (!isMiniApp || !publishedSlapId) {
            return;
        }
        if (!suppressDialogOpen) {
            setOpen(true);
        }
    }, [isMiniApp, publishedSlapId, suppressDialogOpen]);

    // Add effect to process transaction logs
    useEffect(() => {
        if (!slapTransactionResult || !slapTransactionHash) return;

        // Skip if we've already processed this transaction
        if (processedTxHashesRef.current.has(slapTransactionHash)) {
            return;
        }

        console.log('Processing slap transaction logs');
        let activitiesAdded = false;
        let firstSlapId: SlapId | null = null;
        let firstStickerId: StickerDesignId | null = null;

        slapTransactionResult.logs.forEach((log: any) => {
            // Check if this is a StickerSlapped event
            if (log.topics[0] === '0x8d0fc1b8dc2e3bf98f62966d617f50d20cd79c67a3a8c1cea930d8f400b76791') {
                const placeId = hexToNumber(log.topics[1]);
                const stickerId = hexToNumber(log.topics[2]);
                const player = log.topics[3];
                const slapId = hexToNumber(log.data.slice(0, 66)); // First 32 bytes
                const size = hexToNumber(log.data.slice(66, 130)); // Next 32 bytes

                console.log('Extracted slap data:', {
                    placeId,
                    stickerId,
                    player,
                    slapId,
                    size
                });

                if (firstSlapId === null) {
                    firstSlapId = slapId;
                    firstStickerId = stickerId;
                }
                // Add the slap activity using the callback (mark as user-created for resource locator)
                addSlapActivity(slapId, placeId, stickerId, size, true);
                activitiesAdded = true;
            }
        });

        // Only mark as processed and update UI if we actually found and processed activities
        if (activitiesAdded) {
            processedTxHashesRef.current.add(slapTransactionHash);
            setPublishStage('published');
            if (firstSlapId !== null) {
                setPublishedSlapId(firstSlapId);
                setShowSharePrompt(true);
                if (firstStickerId !== null) {
                    const sticker = getStickerDesign({ boardRef, stickerId: firstStickerId });
                    setShareStickerName(sticker?.name ?? null);
                    setSharePublisherEns(
                        sticker?.currentPublisherEns ?? sticker?.originalPublisherEns ?? null
                    );
                } else {
                    setShareStickerName(null);
                    setSharePublisherEns(null);
                }
            }
            handleDidSubmitTransaction();
        }
    }, [slapTransactionResult, slapTransactionHash, addSlapActivity, handleDidSubmitTransaction, boardRef]);

    useEffect(() => {
        if (previewPollTimeout.current) {
            clearTimeout(previewPollTimeout.current);
            previewPollTimeout.current = null;
        }
        if (previewCountdownInterval.current) {
            clearInterval(previewCountdownInterval.current);
            previewCountdownInterval.current = null;
        }
        if (!publishedSlapId) {
            setSharePreviewStatus('idle');
            setSharePreviewUrl(null);
            setShareCheckCount(0);
            setShareNextCheckIn(null);
            setShareResourceUrl(null);
            return;
        }
        const resourceUrl = buildLocateUrl({
            resourceType: 'slap',
            resourceId: publishedSlapId,
            format: 'og',
        });
        if (!resourceUrl) {
            setSharePreviewStatus('error');
            setShareResourceUrl(null);
            return;
        }
        setSharePreviewStatus('loading');
        setSharePreviewUrl(null);
        setShareCheckCount(0);
        setShareTimedOut(false);
        pollStartedAtRef.current = Date.now();

        let isCancelled = false;
        setShareResourceUrl(resourceUrl);

        const scheduleNext = (delayMs: number) => {
            if (isCancelled) {
                return;
            }
            const startedAt = pollStartedAtRef.current;
            if (startedAt && Date.now() - startedAt >= 180000) {
                setSharePreviewStatus('error');
                setShareNextCheckIn(null);
                setShareTimedOut(true);
                if (previewPollTimeout.current) {
                    clearTimeout(previewPollTimeout.current);
                    previewPollTimeout.current = null;
                }
                if (previewCountdownInterval.current) {
                    clearInterval(previewCountdownInterval.current);
                    previewCountdownInterval.current = null;
                }
                return;
            }
            const delaySeconds = Math.max(1, Math.round(delayMs / 1000));
            setShareNextCheckIn(delaySeconds);
            if (previewCountdownInterval.current) {
                clearInterval(previewCountdownInterval.current);
            }
            previewCountdownInterval.current = setInterval(() => {
                setShareNextCheckIn(prev => {
                    if (prev === null) {
                        return prev;
                    }
                    if (prev <= 1) {
                        if (previewCountdownInterval.current) {
                            clearInterval(previewCountdownInterval.current);
                            previewCountdownInterval.current = null;
                        }
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            previewPollTimeout.current = setTimeout(poll, delayMs);
        };

        const poll = async () => {
            if (isCancelled) {
                return;
            }
            const startedAt = pollStartedAtRef.current;
            if (startedAt && Date.now() - startedAt >= 180000) {
                setSharePreviewStatus('error');
                setShareNextCheckIn(null);
                setShareTimedOut(true);
                if (previewPollTimeout.current) {
                    clearTimeout(previewPollTimeout.current);
                    previewPollTimeout.current = null;
                }
                if (previewCountdownInterval.current) {
                    clearInterval(previewCountdownInterval.current);
                    previewCountdownInterval.current = null;
                }
                return;
            }
            try {
                setShareCheckCount(prev => prev + 1);
                const response = await fetch(resourceUrl, { cache: 'no-store' });
                if (isCancelled) {
                    return;
                }
                if (response.status === 202) {
                    scheduleNext(5000);
                    return;
                }
                if (response.ok) {
                    setSharePreviewUrl(response.url);
                    setSharePreviewStatus('ready');
                    setShareNextCheckIn(null);
                } else {
                    setSharePreviewStatus('error');
                    if (response.status >= 500) {
                        scheduleNext(12000);
                    } else {
                        scheduleNext(5000);
                    }
                }
            } catch (_err) {
                if (!isCancelled) {
                    scheduleNext(12000);
                }
            }
        };

        poll();

        return () => {
            isCancelled = true;
            if (previewPollTimeout.current) {
                clearTimeout(previewPollTimeout.current);
                previewPollTimeout.current = null;
            }
            if (previewCountdownInterval.current) {
                clearInterval(previewCountdownInterval.current);
                previewCountdownInterval.current = null;
            }
            pollStartedAtRef.current = null;
        };
    }, [publishedSlapId]);

    useEffect(() => {
        if (publishedSlapId) {
            setShowSharePrompt(true);
        } else {
            setShowSharePrompt(false);
        }
    }, [publishedSlapId]);

    const didClickDelete = (slapId: SlapId) => {
        setOpen(false)
        handleDeletePendingSlap(slapId)
    }

    const { data: slapPreCheckData,
            error: slapPreCheckError,
            status: slapPreCheckStatus,
            isFetched: slapPreCheckIsFetched,
            refetch: refetchSlapPreCheck
    } = useReadContract({
        abi,
        address: StickerChainContractAddress || '0x0',
        functionName: 'checkSlaps',
        query: {enabled: false},
        args: [playerAddress || zeroAddress, slapSpecs, selectedObjectives]
    })

    const { openConnectModal } = useConnectModal();

    const updatePreCheckWarnings = useCallback((warnings: SlapWarning[]) => {
        setPreCheckWarnings((prev) => {
            const sameLength = prev.length === warnings.length;
            const sameContent = sameLength && prev.every((item, index) => {
                const next = warnings[index];
                return (
                    item.issueCode === next.issueCode &&
                    item.recordId === next.recordId &&
                    item.value === next.value
                );
            });
            if (sameContent) {
                return prev;
            }
            return warnings;
        });
        if (onPreCheckWarningsChange) {
            onPreCheckWarningsChange(warnings);
        }
    }, [onPreCheckWarningsChange]);

    useEffect(() => {
        if (!open && !isMiniApp && !suppressDialogOpen && preCheckWarnings.length > 0) {
            updatePreCheckWarnings([]);
        }
    }, [open, isMiniApp, suppressDialogOpen, preCheckWarnings.length, updatePreCheckWarnings]);

    const buildShareMessage = useCallback(() => {
        if (!publishedSlapId) {
            return null;
        }
        const shareUrl = buildViewUrl({
            resourceType: 'slap',
            id: publishedSlapId,
            siteUrl: siteOrigin || undefined,
        });
        const nameLabel = shareStickerName ? `"${shareStickerName}" ` : '';
        const message = `I just slapped ${nameLabel}Slap #${publishedSlapId} on StickerChain: ${shareUrl}`;
        return { message, shareUrl };
    }, [publishedSlapId, shareStickerName, siteOrigin]);

    const handleShareSlapFarcaster = useCallback(() => {
        const shareData = buildShareMessage();
        if (!shareData) {
            return;
        }
        const { message, shareUrl } = shareData;

        if (isMiniApp) {
            const embeds: [string] = [shareUrl];
            console.log('Opening Farcaster share composer', {
                slapId: publishedSlapId,
                stickerName: shareStickerName,
                previewUrl: sharePreviewUrl,
                shareUrl,
                message,
                embeds,
            });
            setShareComposerBusy(true);
            void loadFarcasterSdk()
                .then((loadedSdk) => {
                    const composeAction = loadedSdk?.actions?.composeCast;
                    if (!composeAction) {
                        return;
                    }
                    return composeAction({
                        text: message,
                        embeds,
                    });
                })
                .catch(err => {
                    console.error('Unable to open Farcaster composer', err);
                })
                .finally(() => {
                    setShareComposerBusy(false);
                });
            return;
        }

        if (typeof window === 'undefined') {
            return;
        }
        const params = new URLSearchParams({ text: message });
        params.append('embeds[]', shareUrl);
        
        const shareTarget = `https://farcaster.xyz/~/compose?${params.toString()}`;
        window.open(shareTarget, '_blank', 'noopener,noreferrer');
    }, [buildShareMessage, isMiniApp, publishedSlapId, sharePreviewUrl, shareStickerName]);

    const handleShareSlapX = useCallback(() => {
        const shareData = buildShareMessage();
        if (!shareData) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }
        const shareTarget = `https://x.com/intent/tweet?text=${encodeURIComponent(shareData.message)}`;
        window.open(shareTarget, '_blank', 'noopener,noreferrer');
    }, [buildShareMessage]);

    const setPlayerOnPendingSlaps = useCallback(() => {
        if (pendingSlaps === null || !playerAddress || !boardRef.current) {
            return;
        }
        pendingSlaps.forEach(slap => {
            setDraftSlapPlayer({
                boardRef,
                slapId: slap.id,
                player: playerAddress
            });
        });
    }, [boardRef, pendingSlaps, playerAddress]);

    const proposeSlapTransactionToWallet = useCallback(() => {
        if (StickerChainContractAddress === null) {
            console.log('Error StickerChainContractAddress is null')
            return false
        }
        if (pendingSlaps === null) {
            console.log('Error pendingSlaps is null')
            return false
        }
        console.log(`Starting slaps. Count: ${pendingSlaps.length}`)
        const slapSpecs : NewSlapOnchain[] = pendingSlapsOnchainFormat(pendingSlaps)
        console.log(`Proposing transaction to contract ${StickerChainContractAddress}`)
        console.log(`Proposed objectives: ${selectedObjectives}`)
        setPublishStage('awaiting_tx');
        writeContract({
            abi,
            address: StickerChainContractAddress,
            functionName: 'slap',
            value: totalBaseTokenCost,
            args: [slapSpecs, selectedObjectives]
         },
        {
            onSuccess: (data, variables, context) => {
                console.log('Transaction submitted. Variables:', variables);
                setPublishStage('tx_sent');
                setTransactionError(null); // Clear error on success
            },
            onError: (error, variables, context) => {
                setPublishStage('new');
                console.error('Transaction rejected. Variables:', variables);
                setTransactionError(error.message); // Set error message
                setOpen(false);
            }
        })
        console.log(`called writeContract `)
    }, [pendingSlaps, pendingSlapsOnchainFormat, selectedObjectives, totalBaseTokenCost, writeContract]);

    const handleDidClickExecute = useCallback(async () => {
        console.log('Executing slaps')
        if (pendingSlaps === null) {
            return
        }

        // Clear existing warnings before new check
        updatePreCheckWarnings([]);

        // Check if the user is not logged in
        if (!playerAddress || playerAddress === zeroAddress) {
            console.log('User not logged in, opening connect modal')
            if (openConnectModal) {
                openConnectModal()
            } else {
                console.log('openConnectModal is not available')
            }
            return
        }

        setPlayerOnPendingSlaps()

        // Validate slaps before executing
        console.log(`Performing slap pre-check`)
        try {
            const { data: preCheckData, error: preCheckError } = await refetchSlapPreCheck()

            if (preCheckError) {
                console.error("Slap pre-check failed:", preCheckError)
                // Handle the error (e.g., show an error message to the user)
                return
            }

            console.log("Slap pre-check result:", preCheckData)

            if (preCheckData && preCheckData.length > 0) {
                console.log("Slap pre-check found errors:")
                preCheckData.forEach((error, index) => {
                    console.log(`Error ${index + 1}:`, error)
                })
                updatePreCheckWarnings(preCheckData as SlapWarning[]);
                return
            }

            // If pre-check is successful (empty array), proceed with actual execution
            updatePreCheckWarnings([])
            const requestTransactionResult = proposeSlapTransactionToWallet();
            console.log(`Request transaction result: ${requestTransactionResult}`)
        } catch (error) {
            console.error("Error during slap pre-check:", error)
            // Handle any unexpected errors
        }
    }, [pendingSlaps, playerAddress, openConnectModal, setPlayerOnPendingSlaps, refetchSlapPreCheck, proposeSlapTransactionToWallet, updatePreCheckWarnings]);

    useEffect(() => {
        if (setOpenTimestamp != lastOpenedAt) {
            resetSlapTransaction()
            setLastOpenedAt(setOpenTimestamp)
            if (autoExecuteOnOpen) {
                if (lastAutoExecuteRef.current === setOpenTimestamp) {
                    return
                }
                lastAutoExecuteRef.current = setOpenTimestamp
                if (!isMiniApp && !suppressDialogOpen) {
                    setOpen(true)
                }
                handleDidClickExecute()
                return
            }
            if (!suppressDialogOpen) {
                setOpen(true)
            }
        }
    }, [
        setOpenTimestamp,
        lastOpenedAt,
        resetSlapTransaction,
        autoExecuteOnOpen,
        isMiniApp,
        suppressDialogOpen,
        handleDidClickExecute
    ])

    const handleObjectiveToggle = (objectiveId: string) => {
        setSelectedObjectives(prev =>
            prev.includes(BigInt(objectiveId))
                ? prev.filter(id => id !== BigInt(objectiveId))
                : [...prev, BigInt(objectiveId)]
        );
    }

    const hasPendingSlaps = !!pendingSlaps && pendingSlaps.length > 0

    const sharePreviewDisplayUrl = shareResourceUrl?.replace(/\/og$/, '/sq') ?? null
    const showShareOverlay = Boolean(publishedSlapId && showSharePrompt)

    useEffect(() => {
        if (onShareOverlayChange) {
            onShareOverlayChange(Boolean(showShareOverlay));
        }
    }, [onShareOverlayChange, showShareOverlay]);

    if (!showShareOverlay) {
        return null
    }

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/90 px-4 py-8">
            <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-900 p-6 text-white shadow-xl space-y-5 relative">
                {sharePreviewStatus === 'ready' && (
                    <button
                        type="button"
                        aria-label="Close"
                        className="absolute right-4 top-4 text-lg text-slate-300 hover:text-white transition"
                        onClick={handleCloseSharePanel}
                    >
                        X
                    </button>
                )}
                <div className="text-center">
                    <p className="text-xs uppercase tracking-wide text-slate-400">
                        {sharePreviewStatus === 'ready' ? 'Shareable Preview Image' : 'Generating Preview'}
                    </p>
                </div>
                <SlapPublishSharePanel
                    slapId={publishedSlapId}
                    sharePreviewStatus={sharePreviewStatus}
                    shareCheckCount={shareCheckCount}
                    shareNextCheckIn={shareNextCheckIn}
                    shareResourceUrl={shareResourceUrl}
                    sharePreviewUrl={sharePreviewUrl}
                    sharePreviewDisplayUrl={sharePreviewDisplayUrl}
                    stickerName={shareStickerName}
                    publisherEns={sharePublisherEns}
                    shareComposerBusy={shareComposerBusy}
                    isMiniApp={isMiniApp}
                    hasTimedOut={shareTimedOut}
                    onShareX={handleShareSlapX}
                    onShareFarcaster={handleShareSlapFarcaster}
                    onClose={handleCloseSharePanel}
                />
            </div>
        </div>
    )
}
